import React from 'react';
import styled from 'styled-components';

const HeroContainer = styled.section`
  id: hero;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: 
    linear-gradient(to right, ${({ theme }) => theme.colors.overlay.dark}CC 0%, ${({ theme }) => theme.colors.overlay.dark}66 50%, ${({ theme }) => theme.colors.overlay.dark}99 100%),
    linear-gradient(135deg, ${({ theme }) => theme.colors.accent.primary}4D 0%, ${({ theme }) => theme.colors.accent.secondary}4D 100%),
    url('/images/hero/hero-manufacturing-background.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.lg}`};
  margin-top: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.md}`};
    min-height: 90vh;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.sm}`};
    min-height: 85vh;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing['2xl']};
  align-items: center;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const HeroText = styled.div`
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    order: 2;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes['6xl']};
  font-weight: 400;
  line-height: 1.1;
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const HeroTagline = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 300;
  color: ${({ theme }) => theme.colors.white};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  letter-spacing: 0.25px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const CrisisStatement = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.white};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: 1.4;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const HeroDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 300;
  color: ${({ theme }) => theme.colors.white};
  margin: 0 0 ${({ theme }) => theme.spacing['2xl']} 0;
  line-height: 1.6;
  max-width: 600px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    justify-content: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: 500;
  text-decoration: none;
  border-radius: 8px;
  transition: ${({ theme }) => theme.transitions.smooth};
  box-shadow: ${({ theme }) => theme.shadows.md};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.accent.gray};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    outline: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 300px;
  }
`;

const SecondaryCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: 400;
  text-decoration: none;
  border: 2px solid ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  transition: ${({ theme }) => theme.transitions.smooth};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.primary.main};
    outline: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 300px;
  }
`;

const HeroImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    order: 1;
  }
`;

const ProfileImage = styled.img`
  width: 450px;
  height: 450px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 4px solid ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 380px;
    height: 380px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 320px;
    height: 320px;
  }
`;



const HeroSection = () => {
  console.log('HeroSection updated - contrast and colors fixed');
  
  const handleScrollToContact = (e) => {
    e.preventDefault();
    const target = document.getElementById('contact');
    if (target) {
      const headerHeight = 80;
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleScrollToServices = (e) => {
    e.preventDefault();
    const target = document.getElementById('services');
    if (target) {
      const headerHeight = 80;
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <HeroContainer id="hero">
      <HeroContent>
        <HeroText>
          <HeroTitle>Antony Haines</HeroTitle>
          <HeroTagline>Fractional CTO for Manufacturing Excellence</HeroTagline>
          
          <CrisisStatement>
            Manufacturing downtime costs $260,000/hour.
            <br />
            Is your IT/OT convergence ready for Industry 5.0?
          </CrisisStatement>
          
          <HeroDescription>
            Transforming US mid-market manufacturers through strategic IT/OT integration. 
            Fortune 500 experience, proven 85% improvement results, and multi-dimensional 
            expertise that turns operational crises into competitive advantages.
          </HeroDescription>
          
          <CTAContainer>
            <PrimaryCTA 
              href="#contact" 
              onClick={handleScrollToContact}
            >
              Schedule Free Zoom Consultation
            </PrimaryCTA>
            <SecondaryCTA 
              href="#services" 
              onClick={handleScrollToServices}
            >
              View Services
            </SecondaryCTA>
          </CTAContainer>
        </HeroText>
        
        <HeroImage>
          <ProfileImage 
            src="/images/antony-haines-professional-headshot.jpg"
            alt="Antony Haines - Fractional CTO for Manufacturing Excellence"
          />
        </HeroImage>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;